/**
 * data/ — the locally-ingested dataset schema + a small typed fixture set.
 * Read-only at runtime. The real Gen 1–3 dataset + sprites are produced by the
 * ingest script into data/generated/ (gitignored). NEVER calls PokeAPI at runtime.
 */
import type { SpeciesData, MoveData } from '@/shared';
import type { DataIndex } from './schema.js';
import { FIXTURE_SPECIES, FIXTURE_MOVES } from './fixtures.js';

export * from './schema.js';
export * from './fixtures.js';
export {
  loadGeneratedDataset,
  loadDataIndex,
  loadDataset,
  loadRenderIndex,
  GENERATED_DATASET_URL,
  GENERATED_RENDERS_URL,
  _resetGeneratedDatasetCache,
} from './loader.js';

/**
 * Build an O(1) lookup index over a species + move list.
 *
 * Defaults to the hermetic FIXTURES so unit tests stay deterministic and need no
 * generated artifacts. At runtime the browser prefers the generated Gen 1-3 dataset
 * by passing `loadGeneratedDataset()`'s species/moves in (see `loadDataIndex`), else
 * falls back here to fixtures.
 */
export function buildDataIndex(
  species: readonly SpeciesData[] = FIXTURE_SPECIES,
  moves: readonly MoveData[] = FIXTURE_MOVES,
): DataIndex {
  const byId = new Map(species.map((s) => [s.id, s]));
  const byDex = new Map(species.map((s) => [s.dexNo, s]));
  const moveById = new Map(moves.map((m) => [m.id, m]));
  return {
    speciesById: (id) => byId.get(id),
    speciesByDexNo: (dexNo) => byDex.get(dexNo),
    moveById: (id) => moveById.get(id),
  };
}
